import React from 'react';
import { Mail, Github, Twitter, Linkedin, ExternalLink } from 'lucide-react';
import { useLocation } from 'react-router-dom';

export default function Footer() {
  const location = useLocation();
  const isHealth = location.pathname.startsWith('/health');

  return (
    <footer className="bg-white text-slate-900 dark:bg-[#0F172A] dark:text-white py-24 px-8 mt-32 rounded-t-[4rem]">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-16 lg:gap-24">
          <div className="space-y-8 col-span-1 md:col-span-1">
            <div className="flex flex-col">
              <span className="text-3xl font-black tracking-tighter leading-none">
                <span className="text-emerald-800 dark:text-emerald-400">META</span><span className="text-black dark:text-purple-400">.UTILIX</span>
              </span>
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-2">{isHealth ? 'Health' : 'Institutional'} Grade Suite</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed font-medium">
              Precision-engineered utilities designed for maximum accuracy, total transparency, and extreme performance.
            </p>
            <div className="flex gap-4">
              {[Twitter, Linkedin, Github].map((Icon, i) => (
                <div key={i} className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center text-slate-400 hover:text-brand hover:bg-slate-700 cursor-pointer transition-all border border-slate-700">
                  <Icon size={18} />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 mb-8">Investment Suite</h4>
            <ul className="space-y-4 text-sm font-bold tracking-tight text-white/60">
              <li><a href="#" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">SIP Planner</a></li>
              <li><a href="#" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">Lumpsum Growth</a></li>
              <li><a href="#" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">SWP Strategy</a></li>
              <li><a href="#" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">Mutual Fund Engine</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 mb-8">Analytics Engine</h4>
            <ul className="space-y-4 text-sm font-bold tracking-tight text-white/60">
              <li><a href="#" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">CAGR Analyzer</a></li>
              <li><a href="#" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">XIRR Solver</a></li>
              <li><a href="#" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">IRR Analytics</a></li>
              <li><a href="#" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">Portfolio Recon</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 mb-8">Institutional</h4>
            <ul className="space-y-4 text-sm font-bold tracking-tight text-white/60">
            <li><a href="/privacy" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">Privacy Codex</a></li>
            <li><a href="/terms" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">Terms of Protocol</a></li>
              <li><a href="#" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide">Cookie Policy</a></li>
              <li><a href="#" className="hover:text-brand transition-colors uppercase text-[12px] tracking-wide flex items-center gap-2">Affiliate Portal <ExternalLink size={12} /></a></li>
            </ul>
          </div>
        </div>

        <div className="mt-24 pt-12 border-t border-slate-800 flex flex-col gap-8">
          <div className="flex flex-wrap gap-x-8 gap-y-2 justify-center md:justify-start pb-2">
            <a href="/about" className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-emerald-400 dark:hover:text-purple-400 transition-colors">About Us</a>
            <a href="/contact" className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-emerald-400 dark:hover:text-purple-400 transition-colors">Contact Us</a>
            <a href="/privacy" className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-emerald-400 dark:hover:text-purple-400 transition-colors">Privacy Policy</a>
            <a href="/terms" className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-emerald-400 dark:hover:text-purple-400 transition-colors">Terms of Service</a>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex flex-col gap-4 items-center md:items-start max-w-xl">
              <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.3em]">
                © {new Date().getFullYear()} meta.utilix. All rights reserved.
              </p>
              <p className="text-slate-400 text-[10px] font-medium leading-relaxed p-4 bg-slate-900/50 rounded-2xl border border-slate-800">
                Disclaimer: Calculations and data processing metrics provided by MetaUtilix are for informational and educational purposes only. They do not constitute formal financial, technical, or professional advice.
              </p>
              {isHealth ? (
                 <p className="text-emerald-500/80 text-[10px] font-black uppercase tracking-widest leading-relaxed p-4 bg-emerald-500/5 rounded-2xl border border-emerald-500/10">
                  Medical Disclaimer: This tool is for educational and informational purposes only. It does not substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition.
                </p>
              ) : (
                <p className="text-slate-600 text-[9px] font-bold uppercase tracking-widest leading-relaxed">
                  Legal Disclaimer: All information provided by meta.utilix calculators is intended for educational and illustrative purposes only. All calculations and estimations are based on user inputs and generic mathematical formulae. We are not qualified financial, legal, or tax professionals. Accuracy is not guaranteed. Consult with a certified specialist (CPA, CA, Attorney) before making significant decisions.
                </p>
              )}
              <p className="text-slate-700 text-[9px] font-black uppercase tracking-widest italic">Precision v2.1.0_STABLE</p>
            </div>
            <div className="flex items-center gap-10">
              <span className="text-[10px] uppercase font-black tracking-widest text-slate-700">SSL ENCRYPTED</span>
              <span className="text-[10px] uppercase font-black tracking-widest text-slate-700">MATH.JS V11.0</span>
              <div className="bg-slate-800 px-6 py-2 rounded-full border border-slate-700">
                 <span className="text-[10px] uppercase font-black tracking-widest text-brand">100/100 CORE WEB VITALS</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
